import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import { useReward } from 'react-rewards'

import {
  ChannelAvatar,
  Flex,
  PageHeader,
  SimpleSpinner,
} from '@buffer-mono/popcorn'

import { AllChannelsAvatar } from '~publish/components/AllChannelsAvatar'
import { SaveViewButton } from '~publish/components/MainSidebar/components/SavedViewsSection'
import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'

import { CommentsScorePopover } from '../Comments/CommentsScorePopover'
import { MiniCommentScore } from '../components/MiniCommentScore'
import { useCommentsScore } from '../hooks/scoring/useCommentsScore'
import { useIsFirstRender } from '../hooks/ui/useIsFirstRender'
import { getScoreLevel, isHigherLevel } from '../util/commentScoreTheme'
import type { ScoreLevel } from '../util/commentScoreTheme'

import styles from './CommunityPageTitle.module.css'

export const CommunityPageTitle_Channel = graphql(/* GraphQL */ `
  fragment CommunityPageTitle_Channel on Channel @_unmask {
    id
    avatar
    service
    name
  }
`)

/**
 * `displayName` is nullable-optional on the codegen `Channel` these channels
 * still arrive as pre-producer-flip, so it can't live in the required fragment
 * shape yet; it's added back as an optional field the title reads. Collapses
 * into the fragment once the producer flips (CORE-6384).
 */
type CommunityPageTitleChannel = FragmentOf<
  typeof CommunityPageTitle_Channel
> & {
  displayName?: string | null
}

const CommentsScore = ({
  channelIds,
}: {
  channelIds: string[]
}): JSX.Element => {
  const { isFirstRender } = useIsFirstRender()
  const { commentsScore, loading } = useCommentsScore({
    channelIds,
  })
  const scoreValue = commentsScore?.currentPeriodScore.totalValue
  const score = scoreValue ?? 0
  const [isOpen, setIsOpen] = useState(false)
  const [previousLevel, setPreviousLevel] = useState<ScoreLevel | undefined>(
    () => {
      if (scoreValue === undefined) {
        return undefined
      }

      return getScoreLevel(scoreValue)
    },
  )

  const { reward } = useReward('comments-score-celebration', 'confetti', {
    spread: 110,
    elementCount: 180,
    startVelocity: 35,
    lifetime: 80,
    decay: 0.8,
    colors: ['#5E68C2', '#FF3363', '#FFBB00', '#2AFF28', '#2AB6FF', '#EF0583'],
  })

  // Create a stable key that represents the view type and score
  // For single channel: use channelId-score, for multiple: use 'all-channels'-score
  const isSingleChannel = channelIds && channelIds.length === 1
  const scoreKey = isSingleChannel
    ? `${channelIds[0]}-${score}`
    : `all-channels-${score}`

  // Effect to detect level changes
  useEffect(() => {
    if (scoreValue === undefined) {
      // Reset previous level when score is cleared (channel change)
      setPreviousLevel(undefined)
      return
    }

    const currentLevel = getScoreLevel(scoreValue)

    if (
      !isFirstRender &&
      previousLevel !== undefined &&
      currentLevel !== previousLevel &&
      isHigherLevel(currentLevel, previousLevel)
    ) {
      reward()
    }

    setPreviousLevel(currentLevel)
  }, [scoreValue, reward, previousLevel, isFirstRender, channelIds])

  if (loading && !scoreValue) return <SimpleSpinner />

  if (!commentsScore) return <></>

  return (
    <CommentsScorePopover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      commentsScore={commentsScore}
      isAllChannels={!isSingleChannel}
    >
      <Flex direction="row" align="center" className={styles.scoreContainer}>
        <div id="comments-score-celebration" />
        <MiniCommentScore key={scoreKey} score={score} variant="neutral" />
      </Flex>
    </CommentsScorePopover>
  )
}

export function CommunityPageTitle({
  channels,
}: {
  channels: CommunityPageTitleChannel[]
}): JSX.Element {
  const isSingleChannel = channels && channels.length === 1
  const singleChannel = isSingleChannel ? channels[0] : undefined
  const channelIds = useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  // The single-channel ChannelAvatar carries a platform badge, so it needs the
  // looser `comfortable` gap; the clean AllChannelsAvatar tile sits with the
  // default `compact` gap, matching Publish's AllChannels page.
  return (
    <PageHeader.TitleGroup
      leadingGap={isSingleChannel ? 'comfortable' : 'compact'}
    >
      <PageHeader.TitleLeading>
        {isSingleChannel && singleChannel ? (
          <ChannelAvatar
            src={singleChannel.avatar}
            alt={singleChannel.service}
            channel={singleChannel.service}
            size="mid"
            className={styles.channelAvatar}
          />
        ) : (
          <AllChannelsAvatar />
        )}
      </PageHeader.TitleLeading>
      <PageHeader.Title>
        {isSingleChannel && singleChannel ? (
          <span translate="no">{getChannelNameForDisplay(singleChannel)}</span>
        ) : (
          'Community'
        )}
      </PageHeader.Title>
      <PageHeader.TitleAdornments>
        <SaveViewButton />
        <CommentsScore channelIds={channelIds} />
      </PageHeader.TitleAdornments>
    </PageHeader.TitleGroup>
  )
}
